import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Tag } from 'antd';
import { GoogleMap, Marker, OverlayView, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { TbChevronLeft, TbChevronRight, TbInfoCircle, TbMapPin, TbRoute } from 'react-icons/tb';
import { GOOGLE_MAPS_LOADER_OPTIONS, VESSEL_MAP_CENTER, VESSEL_MAP_OPTIONS } from '../../utils/googleMapsConfig';
import { formatRefDate } from '../../utils/formatRefDate';
import { pointType } from './trackingTypes';

const containerStyle = { width: '100%', height: '100%' };
const routeOptions = { strokeOpacity: 0, icons: [
    { icon: { path: 'M 0,-1 0,1', strokeColor: '#fff', strokeOpacity: 1, strokeWeight: 5, scale: 3 }, offset: '0', repeat: '16px' },
    { icon: { path: 'M 0,-1 0,1', strokeColor: '#1565cf', strokeOpacity: 1, strokeWeight: 3, scale: 3 }, offset: '0', repeat: '16px' }
] };

function GoogleRoute({ points, selected, onSelect, onRetry }) {
    const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
    const [map, setMap] = useState(null);
    const [failed, setFailed] = useState(false);
    const container = useRef(null);
    const positions = useMemo(() => points.map(point => ({ lat: point.latitude, lng: point.longitude })), [points]);
    const center = useMemo(() => positions[0] || VESSEL_MAP_CENTER, [positions]);
    const segments = useMemo(() => {
        const grouped = new Map();
        points.forEach((point, index) => {
            if (!grouped.has(point.segment)) grouped.set(point.segment, []);
            grouped.get(point.segment).push(positions[index]);
        });
        return [...grouped.entries()].filter(([, path]) => path.length > 1);
    }, [points, positions]);
    const fitTrack = useCallback(() => {
        if (!map || !positions.length) return;
        if (positions.every(point => point.lat === positions[0].lat && point.lng === positions[0].lng)) { map.setCenter(positions[0]); map.setZoom(9); return; }
        const bounds = new window.google.maps.LatLngBounds();
        positions.forEach(point => bounds.extend(point));
        map.fitBounds(bounds, { top: 65, right: 55, bottom: 55, left: 55 });
    }, [map, positions]);
    useEffect(() => {
        const previous = window.gm_authFailure;
        const authFailure = () => { setFailed(true); if (typeof previous === 'function') previous(); };
        window.gm_authFailure = authFailure;
        return () => { if (window.gm_authFailure === authFailure) window.gm_authFailure = previous; };
    }, []);
    useEffect(() => {
        if (isLoaded) return;
        const timeout = setTimeout(() => setFailed(true), 15000);
        return () => clearTimeout(timeout);
    }, [isLoaded]);
    useEffect(() => {
        fitTrack();
        if (!map || !container.current) return;
        const observer = new ResizeObserver(fitTrack);
        observer.observe(container.current);
        return () => observer.disconnect();
    }, [map, fitTrack]);
    if (loadError || failed) return <div className="lt-map-state" role="status"><TbMapPin /><h3>Google Maps could not be loaded</h3><p>Your position reports are still available in the list.</p><Button onClick={onRetry}>Retry map</Button></div>;
    if (!isLoaded) return <div className="lt-map-loading" role="status"><Spin /><span>Loading Google Maps</span></div>;
    return <div ref={container} className="lt-map" role="group" aria-label="Google Maps GPS reports">
        <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={6} options={VESSEL_MAP_OPTIONS} onLoad={setMap}>
            {segments.map(([segment, path]) => <Polyline key={segment} path={path} options={routeOptions} />)}
            <Marker position={positions[selected]} title={`Selected report ${selected + 1}`} icon={{ url: '/assets/ship-z.png', scaledSize: new window.google.maps.Size(65, 65), anchor: new window.google.maps.Point(32, 58) }} />
            {points.map((point, index) => <OverlayView key={point.refDate} position={positions[index]} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} zIndex={index === selected ? 3 : 2}>
                <button type="button" className={`lt-map-point ${index === selected ? 'selected' : ''} ${index === 0 ? 'first' : ''} ${index === points.length - 1 ? 'last' : ''}`}
                    aria-label={`Select report ${index + 1}, ${formatRefDate(point.refDate)}`} aria-pressed={index === selected} onClick={() => onSelect(index)}>
                    <span>{points.length === 1 ? 'A/B' : index === 0 ? 'A' : index === points.length - 1 ? 'B' : index + 1}</span>
                </button>
            </OverlayView>)}
        </GoogleMap>
        <Button className="lt-fit-track" size="small" onClick={fitTrack} icon={<TbRoute />}>Fit track</Button>
    </div>;
}
GoogleRoute.propTypes = { points: PropTypes.arrayOf(pointType).isRequired, selected: PropTypes.number.isRequired,
    onSelect: PropTypes.func.isRequired, onRetry: PropTypes.func.isRequired };

export default function TrackingMap({ points, selected, onSelect }) {
    const [attempt, setAttempt] = useState(0);
    const current = points[selected];
    return <section className="lt-panel lt-map-panel" aria-label="GPS route">
        <div className="lt-panel-head"><div className="lt-heading-pair"><TbRoute /><div><h2>Reported positions</h2><p>{points.length.toLocaleString()} GPS reports in this period</p></div></div><Tag color="blue" bordered={false}>Saved reports</Tag></div>
        <GoogleRoute key={attempt} points={points} selected={selected} onSelect={onSelect} onRetry={() => setAttempt(value => value + 1)} />
        <div className="lt-map-legend"><span><i className="lt-first-key" />First report</span><span><i className="lt-last-key" />Last report</span></div>
        <div className="lt-report-period"><span>First report: <b>{formatRefDate(points[0].refDate)}</b></span><span>Last report in period: <b>{formatRefDate(points[points.length - 1].refDate)}</b></span></div>
        <div className="lt-selected-report"><div><span className="lt-field-caption">Selected report · {selected + 1} of {points.length}</span><strong>{formatRefDate(current.refDate)}</strong></div>
            <div className="lt-coordinates"><span>Lat <b>{current.latitude.toFixed(6)}</b></span><span>Lon <b>{current.longitude.toFixed(6)}</b></span></div>
            <div className="lt-step-controls"><Button aria-label="Previous report" icon={<TbChevronLeft />} disabled={selected === 0} onClick={() => onSelect(selected - 1)} /><Button aria-label="Next report" icon={<TbChevronRight />} disabled={selected === points.length - 1} onClick={() => onSelect(selected + 1)} /></div>
        </div>
        <p className="lt-map-note"><TbInfoCircle /><span>Lines connect reported positions. Movement between reports is not measured.</span></p>
    </section>;
}
TrackingMap.propTypes = { points: PropTypes.arrayOf(pointType).isRequired, selected: PropTypes.number.isRequired, onSelect: PropTypes.func.isRequired };
